import { useState, useEffect, useMemo } from 'react';
import API from '@/lib/api';
import { vatLabel } from '@/lib/taxDisplay';
import { lijnPrijsVan, stukPrijsVan } from '@/lib/artikelPrijs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { MagnifyingGlass, Plus, Minus, ShoppingCart, Check, X, User, ClipboardText, Barcode, Trash, Key, SignOut, ArrowRight, ChartLineUp, WifiSlash, CloudArrowUp, Star, CaretUp, CaretDown, ImageSquare } from '@phosphor-icons/react';
import BarcodeScanModal from '@/components/BarcodeScanModal';
import VariantPickerModal from '@/components/VariantPickerModal';
import { leesQueue, voegToeAanQueue, syncQueue } from '@/lib/offlineQueue';
import { cacheKlanten, cacheArtikels, getCachedKlanten, getCachedArtikels } from '@/lib/offlineStore';
import { artikelBtw } from '@/utils/btwKlant';

const fmt = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
const VK_STORAGE_KEY = 'verkoop_actieve_verkoper_v1';

function VoorraadBadge({ voorraad }) {
  const v = Number(voorraad || 0);
  const cls = v <= 0 ? 'bg-red-100 text-red-700 border-red-200' : v < 5 ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-green-100 text-green-700 border-green-200';
  const txt = v <= 0 ? 'Leeg' : v < 5 ? `Nog ${v}!` : `${v} st.`;
  return <span className={`text-[11px] px-2 py-0.5 border font-bold ${cls}`}>{txt}</span>;
}

function ArtikelKaart({ a, qty, onAdd, onMin, prijs, prijsStuk, prijsPack, apiBase }) {
  // Vangnet: als de foto niet laadt (bv. verloren bestand na deploy) → nette placeholder
  // i.p.v. een leeg wit vak.
  const [fotoFout, setFotoFout] = useState(false);
  // Toon eenheidsprijs ALLEEN als gebruiker het expliciet ingevuld heeft op het artikel.
  // Voor pack-artikels zonder eenheid_aantal/label → géén automatische "/st" suffix tonen.
  const ea = parseFloat(a.eenheid_aantal) || 0;
  const el = (a.eenheid_label || '').trim();
  const pg = parseInt(a.pack_grootte, 10) || 0;
  // BEIDE prijzen tonen indien beschikbaar (stuk + pack)
  const heeftBeide = pg > 1 && prijsStuk > 0 && prijsPack > 0 && Math.abs(prijsPack - prijsStuk) > 0.001;
  // Eenheidsprijs-regel wordt ALTIJD gereserveerd (ook leeg) zodat alle kaarten
  // exact dezelfde hoogte hebben → symmetrisch raster.
  const eenheidTekst = (ea > 0 && el && prijs > 0 && a.toon_eenheid_prijs !== false)
    ? (() => {
        const up = prijs / ea;
        const upTxt = up >= 0.01 ? up.toFixed(2).replace('.', ',') : up.toFixed(4).replace('.', ',');
        return `(€ ${upTxt} / ${el}) (${ea === Math.floor(ea) ? Math.floor(ea) : ea}/${el})`;
      })()
    : '';
  return (
    <div className={`relative bg-white border-2 transition-all overflow-hidden h-full flex flex-col ${qty > 0 ? 'border-[#002FA7] shadow-lg ring-2 ring-[#002FA7]/20' : 'border-gray-200 hover:border-gray-400 active:border-[#002FA7]'}`} data-testid={`art-kaart-${a.art_code}`}>
      {/* Foto-vak: vaste 4:3-verhouding, object-contain + witte achtergrond →
          elke foto staat gecentreerd en volledig in beeld, nooit gecropt. */}
      <div className="relative shrink-0 border-b border-gray-100" style={{ paddingTop: '75%', background: '#FFFFFF' }}>
        {a.foto_url && !fotoFout ? (
          <img
            src={`${apiBase}${a.foto_url}`}
            alt={a.omschrijving}
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: 'contain', objectPosition: 'center', padding: 8 }}
            loading="lazy"
            onError={() => setFotoFout(true)}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-gray-300" data-testid={`geen-foto-${a.art_code}`}>
            <ImageSquare size={30} weight="duotone" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Geen foto</span>
          </div>
        )}
        <div className="absolute top-1.5 right-1.5"><VoorraadBadge voorraad={a.voorraad} /></div>
      </div>
      <div className="p-3 flex flex-col flex-1">
        <div className="flex items-center gap-1 text-[11px] text-gray-500 font-mono mb-1" style={{ height: 18 }}>
          {qty > 0 && (
            <span className="bg-[#002FA7] text-white text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0" data-testid={`qty-badge-${a.art_code}`}>
              × {qty}
            </span>
          )}
          <span className="truncate">{a.art_code}</span>
          {a.btw !== undefined && <span className="ml-auto shrink-0" data-testid={`sale-article-vat-${a.id}`}>{vatLabel(a.btw)} BTW</span>}
        </div>
        <div className="text-sm font-bold text-gray-900" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word', lineHeight: 1.2, height: '3.6em' }} title={a.omschrijving}>
          {a.omschrijving}
        </div>
        <div className="text-[10px] text-amber-700 font-mono mt-1 truncate" style={{ height: 14, lineHeight: '14px' }} title={eenheidTekst || undefined} data-testid={eenheidTekst ? `eenheid-prijs-${a.art_code}` : undefined}>
          {eenheidTekst}
        </div>
        <div className="mt-auto pt-2 flex flex-col gap-1.5">
          {/* Prijsblok met vaste hoogte: enkele én dubbele prijs lijnen altijd gelijk uit */}
          <div className="flex items-end overflow-hidden" style={{ height: 34 }}>
            {heeftBeide ? (
              <div className="flex items-end justify-between gap-1 w-full min-w-0" data-testid={`prijs-dual-${a.art_code}`}>
                <div className="flex flex-col leading-tight min-w-0">
                  <span className="text-[9px] font-bold uppercase tracking-wide text-gray-500 whitespace-nowrap">Stuk</span>
                  <span className="text-xs font-bold text-gray-700 tabular-nums whitespace-nowrap" data-testid={`prijs-stuk-${a.art_code}`}>{fmt(prijsStuk)}</span>
                </div>
                <div className="flex flex-col leading-tight items-end min-w-0">
                  <span className="text-[9px] font-bold uppercase tracking-wide text-purple-700 whitespace-nowrap">Pak ({pg})</span>
                  <span className="text-sm font-bold text-[#002FA7] tabular-nums whitespace-nowrap" data-testid={`prijs-pak-${a.art_code}`}>{fmt(prijsPack)}</span>
                </div>
              </div>
            ) : (
              <span className="text-base font-bold text-[#002FA7] tabular-nums whitespace-nowrap" data-testid={`prijs-single-${a.art_code}`}>{fmt(prijs)}</span>
            )}
          </div>
          {qty === 0 ? (
            <button className="btn btn-primary w-full inline-flex items-center justify-center font-bold select-none" style={{ minHeight: 40, padding: '0 10px', fontSize: 13 }} onClick={() => onAdd(a)} data-testid={`add-${a.art_code}`}>
              <Plus size={16} weight="bold" />
            </button>
          ) : (
            <div className="flex items-center justify-between gap-1 select-none w-full">
              <button className="bg-gray-100 hover:bg-gray-200 active:bg-gray-300 font-bold flex items-center justify-center flex-1" style={{ height: 40, borderRadius: 6 }} onClick={() => onMin(a)} data-testid={`min-${a.art_code}`}>
                <Minus size={14} weight="bold" />
              </button>
              <span className="font-bold text-base w-10 text-center tabular-nums">{qty}</span>
              <button className="btn btn-primary font-bold flex items-center justify-center flex-1" style={{ height: 40, borderRadius: 6 }} onClick={() => onAdd(a)} data-testid={`add-more-${a.art_code}`}>
                <Plus size={14} weight="bold" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Pincode-unlock overlay ────────────────────────────────────
function VerkoperUnlock({ onReady, isAdmin, adminName }) {
  const [verkopers, setVerkopers] = useState([]);
  const [selected, setSelected] = useState('');
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get('/verkopers').then(r => {
      const actief = (r.data || []).filter(v => v.actief !== false);
      setVerkopers(actief);
      if (actief.length === 1) setSelected(actief[0].id);
    }).catch(() => setVerkopers([]));
  }, []);

  // Admin kan ook zichzelf kiezen als "verkoper" — geen pin nodig (al hoofdaccount-auth gedaan)
  // Dit is de fast-path voor telefonische bestellingen: admin gaat naar /verkoop,
  // kiest zichzelf, en switcht daarna in de header naar de juiste verkoper-namens.
  const adminEntry = isAdmin ? { id: '__admin__', naam: adminName || 'Admin (geen pin)', actief: true, heeft_pincode: false, machtigingen: {} } : null;
  const opties = isAdmin ? [adminEntry, ...verkopers] : verkopers;

  const unlock = async () => {
    setErr('');
    if (!selected) { setErr('Selecteer eerst uw naam.'); return; }
    // Admin zichzelf → skip backend pincode-check (admin is al geauth via /api/auth/me)
    if (selected === '__admin__') {
      const vk = { id: '__admin__', naam: adminName || 'Admin', machtigingen: { bestelbonnen: true, kassa_gebruiken: true, verkooprapport_bekijken: true }, at: Date.now() };
      try { sessionStorage.setItem(VK_STORAGE_KEY, JSON.stringify(vk)); } catch { /* storage-disabled: skip */ }
      onReady(vk);
      return;
    }
    const v = opties.find(x => x.id === selected);
    const heeftPin = !!v?.heeft_pincode;
    if (heeftPin && (!pin || pin.length < 4)) { setErr('Geef uw pincode (4-6 cijfers) in.'); return; }
    setBusy(true);
    try {
      const r = await API.post('/verkopers/pincode-check', { verkoper_id: selected, pincode: heeftPin ? pin : '' });
      const vk = { id: r.data.id, naam: r.data.naam, machtigingen: r.data.machtigingen || {}, at: Date.now() };
      try { sessionStorage.setItem(VK_STORAGE_KEY, JSON.stringify(vk)); } catch { /* storage-disabled: skip */ }
      onReady(vk);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Onjuiste pincode');
      setPin('');
      try { navigator.vibrate?.([30, 50, 30]); } catch { /* niet kritiek */ }
    } finally { setBusy(false); }
  };

  const pinDigit = (d) => {
    if (pin.length >= 6) return;
    setPin(pin + d);
    setErr('');
  };
  const pinBack = () => setPin(pin.slice(0, -1));

  if (verkopers.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="bg-white p-8 max-w-md w-full shadow-2xl">
          <Key size={42} className="mx-auto text-[#002FA7] mb-3" weight="duotone" />
          <h2 className="text-xl font-bold text-center mb-2">Geen verkopers beschikbaar</h2>
          <p className="text-sm text-gray-600 text-center mb-4">Voeg eerst een verkoper met pincode toe via Instellingen → Boekhouding → Verkopers.</p>
          <a href="/instellingen?tab=verkopers" className="btn btn-primary w-full text-center font-bold block">Naar Instellingen</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4" data-testid="verkoper-unlock">
      <div className="bg-white w-full max-w-md shadow-2xl p-6">
        <div className="text-center mb-5">
          <Key size={40} className="mx-auto text-[#002FA7] mb-2" weight="duotone" />
          <h1 className="text-xl font-bold">Verkoop onderweg</h1>
          <p className="text-xs text-gray-500 mt-1">Verkopers loggen in met email/wachtwoord op /login en worden automatisch herkend. Dit scherm is voor admin om namens een verkoper te bestellen.</p>
        </div>

        <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-1">Verkoper</label>
        <select className="field-input w-full mb-4" style={{ minHeight: 48, fontSize: 15 }} value={selected} onChange={e => { setSelected(e.target.value); setPin(''); setErr(''); }} data-testid="verkoper-unlock-select">
          <option value="">— kies —</option>
          {opties.map(v => <option key={v.id} value={v.id}>{v.naam}{v.heeft_pincode === false && v.id !== '__admin__' ? ' (geen pincode)' : ''}{v.id === '__admin__' ? ' 🛡' : ''}</option>)}
        </select>

        {(() => {
          const v = opties.find(x => x.id === selected);
          const heeftPin = !!v?.heeft_pincode;
          // Geen verkoper geselecteerd OF verkoper zonder pincode → toon enkel grote "Start"-knop
          if (!selected || !heeftPin) {
            return (
              <div className="space-y-3">
                {selected && !heeftPin && (
                  <div className="text-[11px] bg-blue-50 border border-blue-200 text-blue-800 px-2 py-1.5 text-center" data-testid="verkoper-unlock-geen-pin">
                    ℹ️ Deze verkoper heeft geen pincode ingesteld — klik direct op <strong>Start</strong>.
                  </div>
                )}
                <button
                  type="button"
                  onClick={unlock}
                  disabled={busy || !selected}
                  className="btn btn-primary w-full font-bold text-base"
                  style={{ minHeight: 56 }}
                  data-testid="verkoper-unlock-zonder-pin"
                >
                  {busy ? '...' : (selected ? '▶ Start verkoop' : 'Kies eerst een verkoper')}
                </button>
              </div>
            );
          }
          // Verkoper MET pincode → toon pincode-veld + numpad
          return (
            <>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-1">Pincode</label>
              <input
                className="field-input w-full text-center tracking-[0.7em] font-mono"
                style={{ minHeight: 56, fontSize: 24 }}
                value={pin.replace(/./g, '•')}
                readOnly
                data-testid="verkoper-unlock-pin-display"
              />
              <div className="grid grid-cols-3 gap-2 mt-3" data-testid="verkoper-unlock-numpad">
                {['1','2','3','4','5','6','7','8','9'].map(d => (
                  <button key={d} type="button" onClick={() => pinDigit(d)} className="bg-gray-100 hover:bg-gray-200 active:bg-gray-300 font-bold text-xl" style={{ minHeight: 56 }} data-testid={`pin-${d}`}>{d}</button>
                ))}
                <button type="button" onClick={pinBack} className="bg-gray-100 hover:bg-gray-200 active:bg-gray-300 font-bold" style={{ minHeight: 56 }} data-testid="pin-back">←</button>
                <button type="button" onClick={() => pinDigit('0')} className="bg-gray-100 hover:bg-gray-200 active:bg-gray-300 font-bold text-xl" style={{ minHeight: 56 }} data-testid="pin-0">0</button>
                <button type="button" onClick={unlock} disabled={busy || !selected || pin.length < 4} className="btn btn-primary font-bold" style={{ minHeight: 56 }} data-testid="pin-enter">
                  {busy ? '...' : 'OK'}
                </button>
              </div>
            </>
          );
        })()}

        {err && <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 text-xs font-bold text-red-800 text-center" data-testid="verkoper-unlock-err">{err}</div>}
      </div>
    </div>
  );
}

export default function VerkoopPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [verkoper, setVerkoper] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(VK_STORAGE_KEY) || 'null');
      // Timeout: 4 uur
      if (saved && saved.at && (Date.now() - saved.at) < 4 * 60 * 60 * 1000) return saved;
    } catch { /* invalid storage: val terug op null */ }
    return null;
  });
  // 🔄 Admin-mode: lijst van verkopers + dropdown om TE bestellen NAMENS een verkoper
  // (typisch telefonische bestelling: verkoper belt admin door, admin maakt bestelbon op zijn naam)
  const [verkopersLijst, setVerkopersLijst] = useState([]);
  const [adminBesteltVoorId, setAdminBesteltVoorId] = useState(''); // verkoper-id geselecteerd door admin
  const [artikels, setArtikels] = useState([]);
  const [klanten, setKlanten] = useState([]);
  const [klantId, setKlantId] = useState('');
  const [klantZoek, setKlantZoek] = useState('');
  const [zoek, setZoek] = useState('');
  const [categorie, setCategorie] = useState('alle');
  // Gsm-detectie: kortere placeholders + geen auto-focus (toetsenbord dekt halve scherm)
  const [isGsm, setIsGsm] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 640 : false));
  useEffect(() => {
    const onResize = () => setIsGsm(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Gepinde categorieën per verkoper — opgeslagen in localStorage (sleutel per verkoper-id)
  const pinKey = verkoper ? `verkoop_gepinde_cats_v1_${verkoper.id}` : null;
  const [gepinde, setGepinde] = useState(() => {
    if (!verkoper) return [];
    try { return JSON.parse(localStorage.getItem(`verkoop_gepinde_cats_v1_${verkoper.id}`) || '[]'); }
    catch { return []; }
  });
  useEffect(() => {
    if (!pinKey) return;
    try { localStorage.setItem(pinKey, JSON.stringify(gepinde)); } catch { /* quota — stilletjes */ }
  }, [gepinde, pinKey]);
  const togglePin = (cat) => {
    if (cat === 'alle') return;
    setGepinde(p => p.includes(cat) ? p.filter(x => x !== cat) : [...p, cat]);
  };
  const [cart, setCart] = useState({});
  const [variantPicker, setVariantPicker] = useState(null);
  const [opmerking, setOpmerking] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [laatsteScan, setLaatsteScan] = useState(null);
  const [toast, setToast] = useState(null);
  const [succesDoc, setSuccesDoc] = useState(null); // {id, totaal, klant_naam} na opslaan — toont succes-sheet
  const [mijnVerkopenOpen, setMijnVerkopenOpen] = useState(false);
  const [mijnVerkopen, setMijnVerkopen] = useState(null); // {verkoper, totaal_omzet, totaal_winst, bons:[]}
  const [mijnVerkopenLoading, setMijnVerkopenLoading] = useState(false);
  const [expandedBon, setExpandedBon] = useState(null);
  const [offlineAantal, setOfflineAantal] = useState(() => leesQueue().length);
  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== 'undefined' ? navigator.onLine : true));
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  // Helper: bepaal eenheid_aantal/label — alleen expliciet ingevuld op artikel
  const getEenheid = (art) => {
    if (!art) return { ea: 0, el: '' };
    const ea = parseFloat(art.eenheid_aantal) || 0;
    const el = (art.eenheid_label || '').trim();
    return { ea, el };
  };
  // Prijsgroep volgt automatisch de geselecteerde klant.
  // Voorrang: 1) PG:X entry in kortingen-tab > 2) kortinggroep-veld > 3) basic (default)
  const klantPrijsgroep = (k) => {
    if (!k) return 'basic';
    const kortingen = k.kortingen || [];
    for (const ko of kortingen) {
      const a = (ko.artikel || '').trim();
      if (a.toLowerCase().startsWith('pg:')) {
        return a.slice(3).trim().toLowerCase();
      }
    }
    return (k.kortinggroep || 'basic').toLowerCase();
  };
  const prijsGroep = klantPrijsgroep(klanten.find(k => k.id === klantId));

  // Verplicht type: bestelbon (per gebruikers-wens)
  const DOC_TYPE = 'bestelbon';
  const DOC_LABEL = 'Bestelbon';

  useEffect(() => {
    if (!verkoper) return;
    // Probeer eerst online te laden. Bij netwerkfout → fallback naar IndexedDB-cache.
    API.get('/artikels')
      .then(r => {
        const arts = r.data || [];
        setArtikels(arts);
        cacheArtikels(arts).catch(() => { /* cache best-effort */ });
      })
      .catch(async () => {
        const cached = await getCachedArtikels().catch(() => []);
        setArtikels(cached || []);
        if ((cached || []).length > 0) {
          setToast({ text: `📦 ${cached.length} artikels geladen uit offline cache`, kind: 'ok' });
        }
      });
    API.get('/klanten')
      .then(r => {
        const ks = r.data || [];
        setKlanten(ks);
        cacheKlanten(ks).catch(() => { /* cache best-effort */ });
      })
      .catch(async () => {
        const cached = await getCachedKlanten().catch(() => []);
        setKlanten(cached || []);
      });
    // Admin → ook lijst van verkopers ophalen voor "bestel namens"-dropdown
    if (isAdmin) {
      API.get('/verkopers').then(r => setVerkopersLijst((r.data || []).filter(v => v.actief !== false))).catch(() => setVerkopersLijst([]));
    }
  }, [verkoper, isAdmin]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  // Offline/online detectie + automatische sync van wachtende POS-bestelbonnen
  useEffect(() => {
    const onOnline = async () => {
      setIsOnline(true);
      const q = leesQueue();
      if (q.length === 0) return;
      try {
        const res = await syncQueue(API);
        setOfflineAantal(leesQueue().length);
        if (res.gesync > 0) {
          setToast({ text: `✓ ${res.gesync} offline bestelbon${res.gesync > 1 ? 'nen' : ''} gesynchroniseerd`, kind: 'ok' });
        }
        if (res.fouten && res.fouten.length > 0) {
          setToast({ text: `${res.fouten.length} bestelbon(nen) konden niet worden gesynchroniseerd — opnieuw proberen zodra mogelijk`, kind: 'err' });
        }
      } catch { /* stilletjes — wordt opnieuw geprobeerd bij volgend online-event */ }
    };
    const onOffline = () => setIsOnline(false);
    const onSwMessage = (e) => { if (e.data?.type === 'online-sync') onOnline(); };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener?.('message', onSwMessage);
    }
    // Eerste sync-poging bij mount (in geval queue bestaat uit vorige sessie)
    if (navigator.onLine && leesQueue().length > 0) onOnline();
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener?.('message', onSwMessage);
      }
    };
  }, []);

  const logout = () => {
    if (Object.keys(cart).length > 0 && !window.confirm('Uw mandje is niet leeg. Toch afmelden en mandje wissen?')) return;
    try { sessionStorage.removeItem(VK_STORAGE_KEY); } catch { /* niet kritiek */ }
    setVerkoper(null);
    setCart({});
    setKlantId('');
  };

  const [mvPeriode, setMvPeriode] = useState(() => { const n = new Date(); return { jaar: n.getFullYear(), maand: n.getMonth() + 1 }; });
  const MAANDEN_KORT = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];
  const loadMijnVerkopen = async (periode) => {
    if (!verkoper) return;
    const p = periode || mvPeriode;
    if (periode) setMvPeriode(periode);
    setMijnVerkopenOpen(true);
    setMijnVerkopenLoading(true);
    try {
      const r = await API.get(`/verkopers/${verkoper.id}/mijn-verkopen`, { params: { jaar: p.jaar, maand: p.maand } });
      setMijnVerkopen(r.data);
    } catch (e) {
      setToast({ text: 'Laden mislukt: ' + (e.response?.data?.detail || e.message), kind: 'err' });
      setMijnVerkopenOpen(false);
    } finally { setMijnVerkopenLoading(false); }
  };

  const categorieen = useMemo(() => {
    const s = new Set();
    artikels.forEach(a => a.categorie && s.add(a.categorie));
    return ['alle', ...[...s].sort()];
  }, [artikels]);

  const geselecteerdeKlant = klanten.find(k => k.id === klantId);
  const klantLabel = (k) => k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim() || k.naam || k.id;
  const filteredKlanten = klantZoek ? klanten.filter(k => klantLabel(k).toLowerCase().includes(klantZoek.toLowerCase())).slice(0, 10) : [];

  const filteredArtikels = useMemo(() => {
    return artikels.filter(a => {
      if (categorie !== 'alle' && a.categorie !== categorie) return false;
      if (zoek) {
        const q = zoek.toLowerCase();
        return (a.art_code || '').toLowerCase().includes(q) || (a.omschrijving || '').toLowerCase().includes(q) || (a.ean || '').toLowerCase().includes(q);
      }
      return true;
    })
    // Sorteer op categorie → art_code zodat artikels uit dezelfde categorie
    // altijd onder elkaar verschijnen (consistent met de Artikels-pagina).
    .sort((a, b) => {
      const catA = a.categorie || 'zzz';
      const catB = b.categorie || 'zzz';
      const c = catA.localeCompare(catB, 'nl-BE');
      if (c !== 0) return c;
      return (a.art_code || '').localeCompare(b.art_code || '', 'nl-BE');
    });
  }, [artikels, categorie, zoek]);

  const getPrijs = (a) => {
    const p = a.prijzen?.[prijsGroep] || a.prijzen?.basic || a.prijzen?.standaard || 0;
    return lijnPrijsVan(a, p);
  };
  // Helpers: stuk- en pack-prijs voor weergave naast elkaar (respecteert prijs_basis)
  const getPrijsStuk = (a) => stukPrijsVan(a, a.prijzen?.[prijsGroep] || a.prijzen?.basic || a.prijzen?.standaard || 0);
  const getPrijsPack = (a) => lijnPrijsVan(a, a.prijzen?.[prijsGroep] || a.prijzen?.basic || a.prijzen?.standaard || 0);

  const add = (a, variant = null) => {
    // Heeft varianten? Open de picker tenzij we al een variant gekregen hebben
    if (!variant && a.varianten_actief && (a.varianten || []).length > 0) {
      setVariantPicker({ artikel: a });
      return;
    }
    setCart(p => {
      const key = variant ? `${a.id}|${variant.combo_key}` : a.id;
      const cur = p[key] || { art: a, variant, qty: 0 };
      return { ...p, [key]: { art: a, variant, qty: cur.qty + 1 } };
    });
  };
  const min = (a, variant = null) => setCart(p => {
    const key = variant ? `${a.id}|${variant.combo_key}` : a.id;
    const cur = p[key] || { art: a, variant, qty: 0 };
    const q = Math.max(0, cur.qty - 1);
    if (q === 0) { const { [key]: _removed, ...rest } = p; return rest; }
    return { ...p, [key]: { art: a, variant, qty: q } };
  });

  const onScan = (code) => {
    const norm = String(code).trim();
    if (!norm) return;
    // Eerst variant-EAN match
    for (const a of artikels) {
      if (!a.varianten_actief) continue;
      const v = (a.varianten || []).find(x => (x.ean || '').trim() === norm || ((x.ean || '').endsWith(norm) && norm.length >= 12));
      if (v) {
        add(a, v);
        return;
      }
    }
    const match = artikels.find(a => (a.ean || '').trim() === norm)
      || artikels.find(a => (a.art_code || '').toLowerCase() === norm.toLowerCase())
      || artikels.find(a => (a.ean || '').endsWith(norm) && norm.length >= 12);
    if (match) {
      add(match);
      setLaatsteScan({ code: norm, label: `${match.art_code} — ${match.omschrijving}` });
      setToast({ text: `✓ ${match.omschrijving} toegevoegd`, kind: 'ok' });
      try { navigator.vibrate?.(60); } catch { /* niet kritiek */ }
    } else {
      setLaatsteScan({ code: norm, label: 'Geen artikel gevonden' });
      setToast({ text: `⚠ Geen artikel voor code ${norm}`, kind: 'warn' });
      try { navigator.vibrate?.([30, 60, 30]); } catch { /* niet kritiek */ }
    }
  };

  const cartItems = Object.values(cart);

  // getPrijs voor cartItem (overschrijft master-prijs met variant.prijs_override indien gezet)
  const getPrijsCart = ({ art, variant }) => {
    if (variant && variant.prijs_override !== null && variant.prijs_override !== undefined) {
      return parseFloat(variant.prijs_override) || 0;
    }
    return getPrijs(art);
  };

  const totaalExcl = cartItems.reduce((s, ci) => s + getPrijsCart(ci) * ci.qty, 0);
  const totaalBtw = cartItems.reduce((s, ci) => s + getPrijsCart(ci) * ci.qty * (artikelBtw(ci.art) / 100), 0);
  const totaal = totaalExcl + totaalBtw;
  const totaalAantal = cartItems.reduce((s, x) => s + x.qty, 0);

  const plaatsen = async () => {
    if (!verkoper) return;
    if (!klantId) { setToast({ text: 'Selecteer eerst een klant.', kind: 'warn' }); return; }
    if (geselecteerdeKlant?.geblokkeerd) {
      setToast({ text: '🚫 Klant is geblokkeerd — verkoop niet toegestaan.', kind: 'warn' });
      return;
    }
    if (cartItems.length === 0) { setToast({ text: 'Voeg minstens 1 artikel toe.', kind: 'warn' }); return; }
    setSubmitting(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const lijnen = cartItems.map(({ art, qty, variant }) => ({
        referentie: variant?.art_code || art.art_code || '',
        omschr: (art.omschrijving || '') + (variant ? ' · ' + Object.values(variant.combo || {}).join(' / ') : ''),
        aantal: qty,
        prijs: getPrijsCart({ art, variant }),
        btw: artikelBtw(art),
        eenheid: art.eenheid === 'pack' ? 'doos' : (art.eenheid || 'st'),
        art_id: art.id,
        ean: variant?.ean || art.ean || '',
        // Variant-info (alleen wanneer aanwezig)
        variant_combo_key: variant?.combo_key,
        variant_combo: variant?.combo,
        variant_art_code: variant?.art_code,
        // Eenheid-info ENKEL kopiëren als gebruiker het expliciet ingevuld heeft
        eenheid_aantal: parseFloat(art.eenheid_aantal) || 0,
        eenheid_label: (art.eenheid_label || '').trim(),
        toon_eenheid_prijs: art.toon_eenheid_prijs !== false,
        pack_grootte: art.pack_grootte || 0,
      }));
      // Bepaal welke verkoper de bestelbon krijgt:
      // - Admin met een verkoper geselecteerd in dropdown → die verkoper (telefonische bestelling)
      // - Admin ZONDER selectie maar als zichzelf (__admin__) → forceer eerst een keuze
      // - Anders → de ingelogde verkoper zelf
      let actieveVerkoperId = verkoper.id;
      let actieveVerkoperNaam = verkoper.naam;
      if (isAdmin && adminBesteltVoorId) {
        const gekozen = verkopersLijst.find(v => v.id === adminBesteltVoorId);
        if (gekozen) {
          actieveVerkoperId = gekozen.id;
          actieveVerkoperNaam = gekozen.naam;
        }
      } else if (verkoper.id === '__admin__') {
        // Admin staat als zichzelf op /verkoop maar koos geen verkoper-namens → maak bestelbon op admin zelf
        // (verkoper_id blijft '__admin__' wat geen commissie genereert — bewust gedrag)
        actieveVerkoperId = '';
        actieveVerkoperNaam = verkoper.naam;
      }
      const payload = {
        type: DOC_TYPE,
        klant_id: klantId,
        datum: today,
        status: 'concept',
        lijnen,
        excl: Math.round(totaalExcl * 100) / 100,
        btw_totaal: Math.round(totaalBtw * 100) / 100,
        totaal: Math.round(totaal * 100) / 100,
        nota: opmerking,
        bron: 'verkoop_onderweg',
        verkoper_id: actieveVerkoperId,
        verkoper_naam: actieveVerkoperNaam,
      };
      const savedTotaal = totaal;
      const savedKlantNaam = geselecteerdeKlant ? klantLabel(geselecteerdeKlant) : '';
      // Online → direct posten. Offline → queue + succes-feedback met offline-badge
      if (!navigator.onLine) {
        const local_id = voegToeAanQueue(payload);
        setCart({}); setOpmerking(''); setCartOpen(false);
        setSuccesDoc({ id: `OFFLINE-${local_id.slice(-6)}`, totaal: savedTotaal, klant_naam: savedKlantNaam, offline: true });
        setOfflineAantal(leesQueue().length);
        try { navigator.vibrate?.(60); } catch { /* niet kritiek */ }
      } else {
        try {
          const res = await API.post('/documenten', payload);
          setCart({}); setOpmerking(''); setCartOpen(false);
          setSuccesDoc({ id: res.data.id, totaal: savedTotaal, klant_naam: savedKlantNaam });
          try { navigator.vibrate?.(60); } catch { /* niet kritiek */ }
        } catch (err) {
          // Netwerk-fout (bv. timeout) → val terug op offline queue
          if (!err.response) {
            const local_id = voegToeAanQueue(payload);
            setCart({}); setOpmerking(''); setCartOpen(false);
            setSuccesDoc({ id: `OFFLINE-${local_id.slice(-6)}`, totaal: savedTotaal, klant_naam: savedKlantNaam, offline: true });
            setOfflineAantal(leesQueue().length);
          } else {
            throw err;
          }
        }
      }
    } catch (e) {
      setToast({ text: 'Plaatsen mislukt: ' + (e.response?.data?.detail || e.message), kind: 'err' });
    } finally { setSubmitting(false); }
  };

  const wisCart = () => {
    if (cartItems.length === 0) return;
    if (window.confirm('Volledige mandje wissen?')) setCart({});
  };

  // Toon "terug naar boven"-knop na 500px scroll + auto-hide header bij scrollen
  const [toonTerugNaarBoven, setToonTerugNaarBoven] = useState(false);
  const [headerZichtbaar, setHeaderZichtbaar] = useState(true);
  useEffect(() => {
    const mainEl = document.querySelector('main[class*="overflow-y-auto"]') || document.scrollingElement || window;
    const getY = () => (mainEl === window ? (window.scrollY || 0) : (mainEl.scrollTop || 0));
    let ticking = false;
    let vorigeY = getY();
    const onScroll = () => {
      if (ticking) return;
      window.requestAnimationFrame(() => {
        const y = getY();
        setToonTerugNaarBoven(y > 500);
        // Auto-hide: scroll naar beneden → verberg; scroll naar boven → toon.
        // Altijd tonen bij top (y<80) om te voorkomen dat hij "blijft hangen" als je snel scrolt.
        if (y < 80) {
          setHeaderZichtbaar(true);
        } else if (y > vorigeY + 5) {
          setHeaderZichtbaar(false);
        } else if (y < vorigeY - 5) {
          setHeaderZichtbaar(true);
        }
        vorigeY = y;
        ticking = false;
      });
      ticking = true;
    };
    mainEl.addEventListener('scroll', onScroll, { passive: true });
    return () => mainEl.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-unlock: als de ingelogde gebruiker zelf een verkoper is (via email-login gekoppeld
  // aan een verkoper-record), spring direct naar verkoop-UI zonder PIN-scherm.
  // Verkopers loggen in met email/wachtwoord op /login — geen aparte PIN meer nodig.
  useEffect(() => {
    if (verkoper) return; // al ingelogd
    if (user?.verkoper_id && user?.verkoper_naam) {
      const vk = {
        id: user.verkoper_id,
        naam: user.verkoper_naam,
        machtigingen: user.machtigingen || { bestelbonnen: true, kassa_gebruiken: true, verkooprapport_bekijken: true },
        at: Date.now(),
      };
      try { sessionStorage.setItem(VK_STORAGE_KEY, JSON.stringify(vk)); } catch { /* noop */ }
      setVerkoper(vk);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Pincode-overlay tot verkoper is ingelogd (admin krijgt nog wel het keuze-scherm voor telefoon-bestellingen)
  if (!verkoper) {
    return <VerkoperUnlock onReady={setVerkoper} isAdmin={isAdmin} adminName={user?.name || user?.email?.split('@')[0] || 'Admin'} />;
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingBottom: cartItems.length > 0 ? 'calc(84px + env(safe-area-inset-bottom, 0px))' : 0, touchAction: 'manipulation' }}>
      {/* Header — sticky met auto-hide: scroll down = verberg, scroll up = toon. Dubbel-tik = naar boven. */}
      <div
        className={`bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30 transition-transform duration-200 will-change-transform ${headerZichtbaar ? 'translate-y-0' : '-translate-y-full'}`}
        data-testid="verkoop-header"
        onDoubleClick={() => {
          const m = document.querySelector('main[class*="overflow-y-auto"]');
          if (m) m.scrollTo({ top: 0, behavior: 'smooth' });
          else window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      >
        {/* Rij 1: verkoper-badge + titel + scan + afmelden (op desktop ook zoekbalk inline) */}
        <div className="px-3 py-2 flex items-center gap-2">
          <div className="inline-flex items-center gap-1.5 bg-[#002FA7] text-white px-2.5 py-1 font-bold text-xs shrink-0" data-testid="verkoper-actief-badge" title={`Ingelogd als ${verkoper.naam}`}>
            <User size={14} weight="bold" />
            <span>{verkoper.naam.split(' ')[0]}</span>
          </div>
          {/* 🔄 Admin-only: dropdown om TE bestellen NAMENS een andere verkoper (telefonische bestellingen) */}
          {isAdmin && verkopersLijst.length > 0 && (
            <div className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 px-1.5 py-0.5 shrink-0" title="Telefonische bestelling — kies voor wie je bestelt zodat commissie correct loopt" data-testid="admin-bestelt-voor-wrap">
              <span className="text-[10px] font-bold text-amber-900 uppercase tracking-wide">Voor:</span>
              <select
                className="text-xs font-bold bg-transparent border-0 focus:outline-none cursor-pointer py-0.5 pr-1"
                style={{ minHeight: 28 }}
                value={adminBesteltVoorId}
                onChange={(e) => setAdminBesteltVoorId(e.target.value)}
                data-testid="admin-bestelt-voor-select"
              >
                <option value="">— mijzelf ({verkoper.naam.split(' ')[0]})</option>
                {verkopersLijst.map(v => (
                  <option key={v.id} value={v.id}>{v.naam}</option>
                ))}
              </select>
            </div>
          )}
          <h1 className="text-sm sm:text-base font-bold text-gray-900 inline-flex items-center gap-1 shrink-0">
            <ClipboardText size={18} weight="duotone" className="text-[#2563EB]" />
            <span className="hidden sm:inline">Nieuwe bestelbon</span>
            <span className="sm:hidden">Bestelbon</span>
          </h1>
          {/* Offline / queue-indicator — zichtbaar zodra offline of er bonnen in de queue staan */}
          {(!isOnline || offlineAantal > 0) && (
            <div
              className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold shrink-0 ${isOnline ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}
              data-testid="verkoop-offline-badge"
              title={isOnline ? `${offlineAantal} wachtend op sync` : 'Geen netwerk — bestelbonnen worden lokaal opgeslagen'}
            >
              <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-amber-500' : 'bg-red-500'}`} />
              {isOnline ? `Sync ${offlineAantal}` : 'Offline'}
              {offlineAantal > 0 && !isOnline && <span className="ml-0.5">({offlineAantal})</span>}
            </div>
          )}
          <div className="flex-1" />
          <button onClick={() => loadMijnVerkopen()} className="inline-flex items-center gap-1.5 bg-green-600 text-white font-bold px-3 shrink-0 hover:opacity-90 active:opacity-80" style={{ minHeight: 44 }} data-testid="verkoop-mijn-btn" title="Mijn verkopen + winst deze maand">
            <ChartLineUp size={18} weight="bold" />
            <span className="hidden sm:inline">Mijn</span>
          </button>
          <button onClick={() => setScanOpen(true)} className="inline-flex items-center gap-1.5 bg-[#002FA7] text-white font-bold px-3 shrink-0 hover:opacity-90 active:opacity-80" style={{ minHeight: 44 }} data-testid="verkoop-scan-btn" title="Barcode scannen">
            <Barcode size={18} weight="bold" />
            <span className="hidden sm:inline">Scan</span>
          </button>
          <button onClick={logout} className="inline-flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold px-2.5 shrink-0" style={{ minHeight: 44 }} title="Afmelden als verkoper" data-testid="verkoop-logout">
            <SignOut size={16} weight="bold" />
          </button>
        </div>
        {/* Rij 2: zoekbalk — volledige breedte op gsm (categorie-dropdown enkel vanaf tablet,
            op gsm zijn de chips eronder sneller). Icoon staat buiten de tekst-padding zodat
            de placeholder niet meer onder de loep valt. */}
        <div className="px-3 pb-2 flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <MagnifyingGlass size={18} weight="bold" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="field-input"
              placeholder={isGsm ? 'Zoek artikel of code…' : 'Zoek artikel, code of EAN...'}
              value={zoek}
              onChange={e => setZoek(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              style={{ minHeight: 46, fontSize: 15, paddingLeft: 40, paddingRight: zoek ? 42 : 12, borderWidth: 2, borderRadius: 6 }}
              data-testid="verkoop-zoek"
            />
            {zoek && (
              <button
                type="button"
                onClick={() => setZoek('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center bg-transparent border-0 text-gray-400 hover:text-red-500 active:text-red-600 cursor-pointer"
                style={{ width: 38, height: 38, padding: 0 }}
                aria-label="Zoekopdracht wissen"
                data-testid="verkoop-zoek-wis"
              >
                <X size={18} weight="bold" />
              </button>
            )}
          </div>
          <select className="field-input shrink-0 hidden sm:block" style={{ maxWidth: 160, minHeight: 46, fontSize: 14 }} value={categorie} onChange={e => setCategorie(e.target.value)} data-testid="verkoop-categorie">
            {categorieen.map(c => <option key={c} value={c}>{c === 'alle' ? 'Alle' : c}</option>)}
          </select>
        </div>
        {/* Resultaat-teller: direct zien of er iets gevonden is zonder te scrollen */}
        {zoek && (
          <div className="px-3 pb-2 flex items-center gap-2 text-[11px] font-bold" data-testid="verkoop-zoek-resultaat">
            <span className={filteredArtikels.length > 0 ? 'text-[#002FA7]' : 'text-red-600'}>
              {filteredArtikels.length} artikel{filteredArtikels.length === 1 ? '' : 's'} gevonden
            </span>
            {categorie !== 'alle' && <span className="text-gray-500 truncate">in “{categorie}”</span>}
            <button
              type="button"
              onClick={() => { setZoek(''); setCategorie('alle'); }}
              className="ml-auto shrink-0 bg-transparent border-0 p-0 text-gray-500 underline cursor-pointer"
              data-testid="verkoop-zoek-reset"
            >
              Filters wissen
            </button>
          </div>
        )}

        {/* Rij 3: horizontaal-scrollbare categorie-chips — sneller dan dropdown op mobiel.
            Gepinde categorieën verschijnen altijd eerst (per verkoper, opgeslagen in localStorage).
            Tik op de ster om te pinnen/ontpinnen. */}
        {categorieen.length > 1 && (
          <div className="relative">
          {/* Veeg-hint rechts op gsm: toont dat er meer categorieën zijn */}
          <div className="pointer-events-none absolute right-0 top-0 bottom-2 w-10 sm:hidden z-10" style={{ background: 'linear-gradient(to right, rgba(255,255,255,0), #FFFFFF 70%)' }} />
          <div className="px-3 pb-2 flex items-center gap-1.5 overflow-x-auto" style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }} data-testid="verkoop-categorie-chips">
            {(() => {
              // Herorden: 'alle' → gepinde → rest (alfabetisch)
              const pinnedSet = new Set(gepinde);
              const rest = categorieen.filter(c => c !== 'alle' && !pinnedSet.has(c));
              const pinnedOrdered = categorieen.filter(c => pinnedSet.has(c));
              const ordered = ['alle', ...pinnedOrdered, ...rest];
              return ordered;
            })().map(c => {
              const isActief = categorie === c;
              const isPinned = gepinde.includes(c);
              const aantal = c === 'alle' ? artikels.length : artikels.filter(a => a.categorie === c).length;
              return (
                <div
                  key={c}
                  className={`shrink-0 inline-flex items-center gap-1 px-2 py-1.5 text-xs font-bold border transition-colors select-none ${isActief ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#002FA7] hover:text-[#002FA7]'}`}
                  style={{ minHeight: 36, borderRadius: 999, whiteSpace: 'nowrap' }}
                  data-testid={`verkoop-chip-${c}`}
                >
                  <button type="button" onClick={() => setCategorie(c)} className="inline-flex items-center gap-1 bg-transparent border-0 p-0 text-inherit font-bold cursor-pointer">
                    {c === 'alle' ? 'Alle' : c}
                    <span className={`text-[10px] px-1 py-0 rounded ${isActief ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>{aantal}</span>
                  </button>
                  {c !== 'alle' && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); togglePin(c); }}
                      className={`ml-0.5 p-0.5 bg-transparent border-0 cursor-pointer ${isPinned ? (isActief ? 'text-yellow-200' : 'text-yellow-500') : 'text-gray-300 hover:text-yellow-500'}`}
                      title={isPinned ? 'Ontpin categorie' : 'Pin favoriet bovenaan'}
                      data-testid={`verkoop-chip-pin-${c}`}
                      style={{ lineHeight: 0 }}
                    >
                      <Star size={13} weight={isPinned ? 'fill' : 'regular'} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          </div>
        )}

        {/* Klant-balk */}
        <div className="px-3 py-2 sm:py-3 bg-white border-t-2 border-amber-400 flex items-center gap-2 flex-wrap" data-testid="verkoop-klant-balk">
          <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
            <User size={18} className="text-amber-700" weight="bold" />
            <span className="text-xs sm:text-sm font-black uppercase tracking-wider text-amber-800">Klant</span>
          </div>
          {geselecteerdeKlant ? (
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-white border-2 border-[#002FA7] text-sm font-bold text-[#002FA7]" data-testid="verkoop-klant-gekozen">
              {klantLabel(geselecteerdeKlant)}
              <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded font-bold" style={{ background: '#FEF3C7', color: '#92400E' }} data-testid="verkoop-prijsgroep-badge" title={`Prijsgroep van klant — actief op artikelen`}>
                {prijsGroep}
              </span>
              <button onClick={() => { setKlantId(''); setKlantZoek(''); }} className="text-gray-400 hover:text-red-500 flex items-center justify-center" style={{ minHeight: 28, minWidth: 28 }}>
                <X size={14} weight="bold" />
              </button>
            </div>
          ) : (
            <div className="relative flex-1 min-w-[180px]" style={{ maxWidth: 480 }}>
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-700 pointer-events-none" weight="bold" />
              <input
                className="w-full font-semibold border-2 border-amber-500 bg-white focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/30 outline-none"
                placeholder={isGsm ? 'Klant zoeken…' : 'Tik hier om klant te zoeken (naam, bedrijf, BTW…)'}
                value={klantZoek}
                onChange={e => setKlantZoek(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                inputMode="search"
                enterKeyHint="search"
                autoComplete="off"
                style={{ minHeight: 46, fontSize: 15, paddingLeft: 36, paddingRight: klantZoek ? 12 : 96, borderRadius: 6 }}
                autoFocus={!geselecteerdeKlant && !isGsm}
                data-testid="verkoop-klant-zoek"
              />
              {!klantZoek && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-amber-200 text-amber-900 pointer-events-none animate-pulse whitespace-nowrap">
                  Start hier ←
                </span>
              )}
              {filteredKlanten.length > 0 && klantZoek && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-amber-400 shadow-xl max-h-72 overflow-y-auto z-30">
                  {filteredKlanten.map(k => (
                    <div key={k.id} className="px-3 py-2.5 hover:bg-[#F0F4FF] active:bg-[#DCE5FF] cursor-pointer text-sm border-b border-gray-100" onClick={() => { setKlantId(k.id); setKlantZoek(''); }} data-testid={`verkoop-klant-opt-${k.id}`}>
                      <div className="font-bold">{klantLabel(k)}</div>
                      {k.stad && <div className="text-[11px] text-gray-500">{k.stad}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="ml-auto hidden sm:inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-gray-500">
            <ClipboardText size={14} className="text-[#2563EB]" weight="bold" />
            Bestelbon
          </div>
        </div>
        {/* Rode banner wanneer gekozen klant geblokkeerd is */}
        {geselecteerdeKlant?.geblokkeerd && (
          <div className="px-3 py-2 bg-red-50 border-t-2 border-red-500 flex items-center gap-2" data-testid="verkoop-klant-geblokkeerd-banner">
            <span className="text-xl shrink-0 leading-none">🚫</span>
            <div className="flex-1 text-xs text-red-900">
              <div className="font-bold">Klant is geblokkeerd — verkoop niet toegestaan</div>
              <div className="text-[11px] mt-0.5">{klantLabel(geselecteerdeKlant)} staat op blokkade.</div>
            </div>
          </div>
        )}
      </div>

      {/* Artikel grid */}
      <div className="p-3">
        {filteredArtikels.length === 0 ? (
          <div className="text-center py-20 text-gray-400 text-sm">Geen artikelen gevonden voor uw zoekopdracht.</div>
        ) : (
          <div className="grid gap-2 sm:gap-3 items-stretch" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gridAutoRows: '1fr' }}>
            {filteredArtikels.map(a => (
              <ArtikelKaart key={a.id} a={a} qty={cart[a.id]?.qty || 0} onAdd={add} onMin={min} prijs={getPrijs(a)} prijsStuk={getPrijsStuk(a)} prijsPack={getPrijsPack(a)} apiBase={apiBase} />
            ))}
          </div>
        )}
      </div>

      {/* Floating "terug naar boven"-knop — verschijnt na 500px scroll */}
      {toonTerugNaarBoven && (
        <button
          onClick={() => {
            const m = document.querySelector('main[class*="overflow-y-auto"]');
            if (m) m.scrollTo({ top: 0, behavior: 'smooth' });
            else window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="fixed right-3 bg-[#002FA7] text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
          style={{
            bottom: cartItems.length > 0 ? 'calc(96px + env(safe-area-inset-bottom, 0px))' : 'calc(20px + env(safe-area-inset-bottom, 0px))',
            width: 48,
            height: 48,
            borderRadius: '50%',
            zIndex: 40,
            boxShadow: '0 4px 12px rgba(0,47,167,0.4)',
          }}
          title="Terug naar boven (dubbelklik ook mogelijk op de header)"
          aria-label="Scroll naar bovenaan de lijst"
          data-testid="verkoop-terug-naar-boven"
        >
          <CaretUp size={22} weight="bold" />
        </button>
      )}

      {/* Sticky cart-bar — verbergen wanneer modal open is zodat hij niet over de submit-knop valt */}
      {cartItems.length > 0 && !cartOpen && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-[#002FA7] text-white shadow-[0_-4px_16px_rgba(0,0,0,0.2)] z-[50]"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          data-testid="verkoop-cart-bar"
        >
          <div className="px-3 py-3 flex items-center gap-2">
            <button className="flex items-center gap-2 flex-1 text-left min-w-0" onClick={() => setCartOpen(true)} style={{ minHeight: 56 }} data-testid="verkoop-cart-open">
              <ShoppingCart size={24} weight="bold" className="shrink-0" />
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider opacity-80 truncate">{cartItems.length} art · {totaalAantal} st</div>
                <div className="text-base font-bold">{fmt(totaal)}</div>
              </div>
            </button>
            <button className="bg-white text-[#002FA7] font-bold inline-flex items-center justify-center gap-1.5 px-3 sm:px-4 shrink-0" onClick={plaatsen} disabled={submitting || !klantId || geselecteerdeKlant?.geblokkeerd} style={{ minHeight: 56, minWidth: 120, opacity: (!klantId || submitting || geselecteerdeKlant?.geblokkeerd) ? 0.5 : 1 }} title={geselecteerdeKlant?.geblokkeerd ? 'Klant is geblokkeerd — verkoop niet toegestaan' : undefined} data-testid="verkoop-plaatsen">
              <Check size={20} weight="bold" />
              <span className="hidden sm:inline">{submitting ? 'Bezig...' : DOC_LABEL}</span>
              <span className="sm:hidden">{submitting ? '...' : 'OK'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Cart-modal — volledige bottom-sheet op GSM met iPhone safe-area */}
      {cartOpen && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setCartOpen(false)}>
          <div
            className="w-full sm:max-w-xl bg-white shadow-2xl flex flex-col sm:rounded-t-lg sm:rounded-b-lg rounded-t-2xl"
            style={{ maxHeight: 'calc(100dvh - env(safe-area-inset-top, 20px))', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-gray-200 bg-[#002FA7] text-white flex items-center justify-between sm:rounded-t-lg rounded-t-2xl">
              <h3 className="text-base font-bold inline-flex items-center gap-2">
                <ShoppingCart size={20} weight="bold" /> Overzicht ({cartItems.length})
              </h3>
              <div className="flex items-center gap-1">
                <button onClick={wisCart} className="text-white/80 hover:text-white px-2 py-1 inline-flex items-center gap-1 text-xs" style={{ minHeight: 36 }} data-testid="verkoop-cart-wissen">
                  <Trash size={14} /> Wis alles
                </button>
                <button onClick={() => setCartOpen(false)} className="text-white hover:opacity-70 flex items-center justify-center" style={{ minHeight: 36, minWidth: 36 }}>
                  <X size={20} weight="bold" />
                </button>
              </div>
            </div>
            <div className="p-3 sm:p-4 overflow-y-auto flex-1 space-y-2" style={{ WebkitOverflowScrolling: 'touch' }}>
              {cartItems.map(({ art, qty }) => (
                <div key={art.id} className="flex items-center gap-2 sm:gap-3 p-2 bg-gray-50 border border-gray-200">
                  {art.foto_url ? (
                    <img src={`${apiBase}${art.foto_url}`} alt="" style={{ width: 44, height: 44, objectFit: 'cover' }} className="shrink-0" />
                  ) : (
                    <div style={{ width: 44, height: 44 }} className="bg-gray-200 flex items-center justify-center text-xl shrink-0">🖼</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{art.omschrijving}</div>
                    {(() => {
                      const { ea, el } = getEenheid(art);
                      const showUnit = ea > 0 && el && getPrijs(art) > 0 && art.toon_eenheid_prijs !== false;
                      return showUnit ? (
                        <div className="text-[11px] text-gray-500 font-mono truncate">{art.art_code} · € {(getPrijs(art) / ea).toFixed(2).replace('.', ',')}/{el} · {ea === Math.floor(ea) ? Math.floor(ea) : ea}/{el}</div>
                      ) : (
                        <div className="text-[11px] text-gray-500 font-mono truncate">{art.art_code} · {fmt(getPrijs(art))}/st</div>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button className="bg-gray-200 hover:bg-gray-300 flex items-center justify-center" style={{ minHeight: 40, minWidth: 40 }} onClick={() => min(art)}><Minus size={14} weight="bold" /></button>
                    <span className="font-bold text-sm w-7 text-center">{qty}</span>
                    <button className="bg-[#002FA7] text-white hover:opacity-90 flex items-center justify-center" style={{ minHeight: 40, minWidth: 40 }} onClick={() => add(art)}><Plus size={14} weight="bold" /></button>
                  </div>
                  <div className="text-xs sm:text-sm font-bold w-16 sm:w-20 text-right shrink-0">{fmt(getPrijs(art) * qty)}</div>
                </div>
              ))}
              <div className="pt-2">
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Opmerking (optioneel)</label>
                <textarea className="field-input" rows={2} value={opmerking} onChange={e => setOpmerking(e.target.value)} placeholder="bv. Leveren tussen 9-12u, contacteer vooraf" style={{ fontSize: 14 }} />
              </div>
            </div>
            <div className="px-4 sm:px-5 py-3 border-t border-gray-200 bg-gray-50 space-y-1.5 shrink-0">
              <div className="flex justify-between text-xs"><span>Subtotaal</span><span className="font-mono">{fmt(totaalExcl)}</span></div>
              <div className="flex justify-between text-xs"><span>BTW</span><span className="font-mono">{fmt(totaalBtw)}</span></div>
              <div className="flex justify-between text-base font-bold"><span>Totaal</span><span className="font-mono text-[#002FA7]">{fmt(totaal)}</span></div>
              <div className="text-[10px] text-gray-500 italic">
                {(() => {
                  const gekozen = isAdmin && adminBesteltVoorId ? verkopersLijst.find(v => v.id === adminBesteltVoorId) : null;
                  return gekozen
                    ? <>Bestelbon wordt aangemaakt op naam van <strong className="text-amber-700">📞 {gekozen.naam}</strong> (door admin namens deze verkoper).</>
                    : <>Bestelbon wordt aangemaakt op naam van <strong className="text-gray-700">{verkoper.naam}</strong>.</>;
                })()}
              </div>
              <button className="btn btn-primary w-full font-bold" onClick={plaatsen} disabled={submitting || !klantId || geselecteerdeKlant?.geblokkeerd} style={{ minHeight: 52 }} title={geselecteerdeKlant?.geblokkeerd ? 'Klant is geblokkeerd — verkoop niet toegestaan' : undefined} data-testid="verkoop-plaatsen-modal">
                {!klantId ? 'Kies eerst een klant' : submitting ? 'Bezig...' : `Bestelbon aanmaken — ${fmt(totaal)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <BarcodeScanModal open={scanOpen} onClose={() => setScanOpen(false)} onScan={onScan} laatsteScan={laatsteScan} />

      {/* Mijn verkopen-sheet — toont eigen bestelbonnen met winst per bon + totaal deze maand */}
      {mijnVerkopenOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => { setMijnVerkopenOpen(false); setExpandedBon(null); }}
          data-testid="mijn-verkopen-sheet"
        >
          <div
            className="w-full sm:max-w-2xl bg-white shadow-2xl flex flex-col rounded-t-2xl sm:rounded-2xl"
            style={{ maxHeight: 'calc(100dvh - env(safe-area-inset-top, 20px))', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 text-white flex items-center justify-between rounded-t-2xl sm:rounded-t-2xl">
              <h3 className="text-base font-bold inline-flex items-center gap-2">
                <ChartLineUp size={20} weight="bold" /> Mijn verkopen — {mijnVerkopen?.verkoper?.naam || verkoper?.naam}
              </h3>
              <button onClick={() => { setMijnVerkopenOpen(false); setExpandedBon(null); }} className="text-white hover:opacity-70 flex items-center justify-center" style={{ minHeight: 36, minWidth: 36 }}>
                <X size={20} weight="bold" />
              </button>
            </div>

            {/* Maand-/jaarkeuze — bv. Januari of Maart */}
            <div className="px-4 py-2 border-b border-gray-200 bg-white flex items-center gap-2" data-testid="mijn-verkopen-periode">
              <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500 shrink-0">Periode</span>
              <select className="field-input text-sm font-semibold min-w-0 flex-1" style={{ width: 'auto' }} value={mvPeriode.maand} onChange={e => loadMijnVerkopen({ ...mvPeriode, maand: parseInt(e.target.value, 10) })} data-testid="mijn-verkopen-maand">
                {MAANDEN_KORT.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select className="field-input text-sm font-semibold shrink-0" style={{ width: 96 }} value={mvPeriode.jaar} onChange={e => loadMijnVerkopen({ ...mvPeriode, jaar: parseInt(e.target.value, 10) })} data-testid="mijn-verkopen-jaar">
                {[0, 1, 2, 3].map(d => new Date().getFullYear() - d).map(j => <option key={j} value={j}>{j}</option>)}
              </select>
            </div>

            {mijnVerkopenLoading ? (
              <div className="p-10 text-center text-sm text-gray-500 italic">Laden...</div>
            ) : !mijnVerkopen ? (
              <div className="p-10 text-center text-sm text-gray-500 italic">Geen data beschikbaar.</div>
            ) : (
              <>
                {/* Maand-KPI's */}
                <div className="px-4 py-3 bg-gradient-to-b from-green-50 to-white grid grid-cols-3 gap-2">
                  <div className="text-center">
                    <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Omzet maand</div>
                    <div className="text-lg font-bold text-gray-900 tabular-nums">{fmt(mijnVerkopen.totaal_omzet)}</div>
                    <div className="text-[10px] text-gray-500">{mijnVerkopen.aantal_bons} bons</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Totaal winst</div>
                    <div className="text-lg font-bold text-green-700 tabular-nums">{fmt(mijnVerkopen.totaal_winst)}</div>
                    <div className="text-[10px] text-gray-500">marge {mijnVerkopen.marge_pct.toFixed(1)}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Kostprijs</div>
                    <div className="text-lg font-bold text-gray-600 tabular-nums">{fmt(mijnVerkopen.totaal_kost)}</div>
                    <div className="text-[10px] text-gray-500">
                      {String(mijnVerkopen.maand).padStart(2, '0')}/{mijnVerkopen.jaar}
                    </div>
                  </div>
                </div>

                {/* Lijst */}
                <div className="overflow-y-auto flex-1 px-3 py-2 space-y-2" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {mijnVerkopen.bons.length === 0 ? (
                    <div className="text-center py-10 text-sm text-gray-500 italic">Geen bestelbonnen in {MAANDEN_KORT[(mvPeriode.maand || 1) - 1]} {mvPeriode.jaar}.</div>
                  ) : (
                    mijnVerkopen.bons.map(b => {
                      const expanded = expandedBon === b.id;
                      return (
                        <div key={b.id} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`mijn-bon-${b.id}`}>
                          <button
                            type="button"
                            onClick={() => setExpandedBon(expanded ? null : b.id)}
                            className="w-full flex items-center gap-2 p-2.5 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 text-[13px] font-bold text-gray-900">
                                <span className="font-mono">{b.id}</span>
                                <span className="text-[10px] font-normal text-gray-500 truncate">{b.datum}</span>
                              </div>
                              <div className="text-[11px] text-gray-500 truncate">{b.klant_naam || b.klant_id || '—'}</div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-[13px] font-bold text-[#002FA7] tabular-nums">{fmt(b.excl)}</div>
                              <div className="text-[11px] text-green-700 font-bold tabular-nums">+{fmt(b.winst)} <span className="text-gray-500 font-normal">({b.marge_pct.toFixed(0)}%)</span></div>
                            </div>
                            <span className="text-gray-400 text-base" style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>›</span>
                          </button>
                          {expanded && (
                            <div className="border-t border-gray-100 bg-gray-50 p-2">
                              <table className="w-full text-[11px]">
                                <thead>
                                  <tr className="text-gray-500 uppercase tracking-wider">
                                    <th className="text-left pb-1 font-bold">Artikel</th>
                                    <th className="text-right pb-1 font-bold w-10">Qty</th>
                                    <th className="text-right pb-1 font-bold">Verkoop</th>
                                    <th className="text-right pb-1 font-bold">Winst</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {b.lijnen.map((l, i) => (
                                    <tr key={i} className="border-t border-gray-200">
                                      <td className="py-1 pr-1">
                                        <div className="font-semibold text-gray-800 truncate" style={{ maxWidth: 180 }}>{l.omschr}</div>
                                        <div className="text-[10px] text-gray-500 font-mono">{l.art_code} · €{l.aankoop.toFixed(2)}/st kost</div>
                                      </td>
                                      <td className="py-1 text-right tabular-nums font-mono">{l.aantal}</td>
                                      <td className="py-1 text-right tabular-nums">{fmt(l.omzet)}</td>
                                      <td className="py-1 text-right tabular-nums font-bold text-green-700">{fmt(l.winst)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Succes-sheet na opslaan — verkoper kiest: nieuwe bestelling, openen, of afsluiten */}
      {succesDoc && (
        <div
          className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
          data-testid="verkoop-succes-sheet"
        >
          <div
            className="w-full sm:max-w-md bg-white shadow-2xl rounded-t-2xl sm:rounded-2xl flex flex-col"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            {/* Groene succes-kop */}
            <div className="px-5 pt-6 pb-5 bg-gradient-to-b from-green-50 to-white text-center rounded-t-2xl">
              <div
                className="mx-auto flex items-center justify-center mb-3"
                style={{
                  width: 72, height: 72, borderRadius: '50%',
                  background: '#16A34A',
                  boxShadow: '0 0 0 6px rgba(22,163,74,0.12), 0 8px 20px rgba(22,163,74,0.3)',
                }}
              >
                <Check size={40} weight="bold" color="#FFFFFF" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">
                Bestelbon opgeslagen
                {succesDoc.offline && (
                  <span className="ml-2 inline-flex items-center gap-1 align-middle text-[11px] font-bold px-2 py-0.5 bg-amber-100 text-amber-800 rounded">
                    <WifiSlash size={11} weight="bold" /> Offline — sync volgt
                  </span>
                )}
              </h2>
              <div className="text-[13px] text-gray-600">
                <span className="font-mono font-bold text-gray-800">{succesDoc.id}</span>
                {succesDoc.klant_naam && <> · {succesDoc.klant_naam}</>}
              </div>
              <div className="text-2xl font-bold text-[#002FA7] mt-2 tabular-nums">{fmt(succesDoc.totaal)}</div>
              <div className="text-[11px] text-gray-500 mt-1">Opgeslagen door <strong>{verkoper?.naam}</strong></div>
            </div>

            {/* Actie-knoppen */}
            <div className="p-4 space-y-2.5 border-t border-gray-100">
              <button
                className="w-full bg-[#002FA7] hover:opacity-90 active:opacity-80 text-white font-bold py-3.5 inline-flex items-center justify-center gap-2 rounded-lg"
                style={{ minHeight: 56 }}
                onClick={() => {
                  // Nieuwe bestelling: succes-sheet sluiten, klant blijft behouden (snel doorwerken)
                  setSuccesDoc(null);
                }}
                data-testid="succes-nieuwe-bestelling"
              >
                <Plus size={20} weight="bold" /> Nieuwe bestelling starten
              </button>
              <button
                className="w-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-900 font-bold py-3 inline-flex items-center justify-center gap-2 rounded-lg"
                style={{ minHeight: 48 }}
                onClick={() => {
                  // Nieuwe klant voor volgende bestelling
                  setSuccesDoc(null);
                  setKlantId('');
                  setKlantZoek('');
                }}
                data-testid="succes-andere-klant"
              >
                <User size={18} weight="bold" /> Bestelling voor andere klant
              </button>
              <button
                className="w-full bg-white border-2 border-gray-200 hover:border-[#002FA7] hover:text-[#002FA7] text-gray-700 font-bold py-3 inline-flex items-center justify-center gap-2 rounded-lg transition-colors"
                style={{ minHeight: 48 }}
                onClick={() => {
                  const id = succesDoc.id;
                  setSuccesDoc(null);
                  navigate(`/documenten/${id}/bewerk`);
                }}
                data-testid="succes-open-bestelbon"
              >
                Bestelbon bekijken / aanpassen <ArrowRight size={16} weight="bold" />
              </button>
              <button
                className="w-full text-gray-500 hover:text-gray-700 text-sm py-2 inline-flex items-center justify-center gap-1"
                onClick={() => setSuccesDoc(null)}
                data-testid="succes-sluiten"
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-[60] px-4 py-2.5 shadow-2xl font-bold text-sm"
          style={{ top: 80, background: toast.kind === 'ok' ? '#16A34A' : toast.kind === 'warn' ? '#F59E0B' : '#DC2626', color: '#fff', minWidth: 260, textAlign: 'center' }}
          data-testid="verkoop-toast"
        >
          {toast.text}
        </div>
      )}

      {/* Variant-picker modal */}
      <VariantPickerModal
        artikel={variantPicker?.artikel}
        open={!!variantPicker}
        onClose={() => setVariantPicker(null)}
        onSelect={(variant) => add(variantPicker.artikel, variant)}
        prijsGroep={prijsGroep || 'kassa'}
      />
    </div>
  );
}
